import { useRef, useState } from "react";
import * as authApi from "../../api/auth";
import { APIConnectionError, APIError, EMAIL_NOT_VERIFIED } from "../../api/errors";
import { RecoverPasswordDialog } from "../../dialogs/RecoverPasswordDialog";
import { useSession } from "../../state/session";
import { takeLoginPrefill, setLoginPrefill } from "../../state/authPrefill";
import { useAuthConfig } from "../../state/useAuthConfig";
import { PasswordInput } from "../../ui/PasswordInput";
import { AuthCard } from "./AuthCard";

/** Where someone without an account asks for one: signup is closed on the
 *  cloud server, so a prospect who lands here would otherwise be stuck. */
const TRIAL_URL = "https://www.insisotech.com/modelbench#trial";

/** login_window.py */
export function LoginScreen() {
  const { login } = useSession();
  const [username, setUsername] = useState(() => takeLoginPrefill());
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [recover, setRecover] = useState(false);
  const pwRef = useRef<HTMLDivElement>(null);
  const config = useAuthConfig();
  const signupEnabled = config.signup_enabled;
  const [unverified, setUnverified] = useState(false);
  const [resent, setResent] = useState(false);

  const submit = async () => {
    if (!username.trim() || !password) { setError("Please fill in all fields."); return; }
    setBusy(true);
    setError("");
    setUnverified(false);
    setResent(false);
    try {
      await login(username.trim(), password);
    } catch (e) {
      setError(e instanceof APIConnectionError ? `Connection error: ${e.detail}` : (e as Error).message);
      setUnverified(e instanceof APIError && e.code === EMAIL_NOT_VERIFIED);
      setBusy(false);
    }
  };

  const resend = async () => {
    try { await authApi.resendVerification(username.trim()); setResent(true); }
    catch (e) { setError((e as Error).message); }
  };

  return (
    <AuthCard>
      <h1>Sign in</h1>
      <div className="sub">Welcome back. Sign in to your workspace.</div>
      <label className="caption" htmlFor="li-user">Username / Email</label>
      <input
        id="li-user" className="input" placeholder="Username or email" autoComplete="username" value={username}
        onChange={(e) => setUsername(e.target.value)} autoFocus
        onKeyDown={(e) => { if (e.key === "Enter") pwRef.current?.querySelector("input")?.focus(); }}
      />
      <label className="caption" htmlFor="li-pass">Password</label>
      <div ref={pwRef}><PasswordInput id="li-pass" value={password} onChange={setPassword} onEnter={submit} /></div>
      <div className="error-text" role="alert" data-testid="login-error">{error}</div>
      {unverified && (resent
        ? <div className="sub" data-testid="resent">If that account exists, a new confirmation email is on its way.</div>
        : <button className="btn flat" onClick={resend} data-testid="resend-verification">Send the confirmation email again</button>)}
      <button className="btn primary" disabled={busy} onClick={submit} data-testid="login-submit">{busy ? "Signing in…" : "Sign In"}</button>
      <div style={{ textAlign: "center" }}>
        {config.email_flows_enabled
          ? <a className="btn flat" href="#/forgot-password" data-testid="forgot">Forgot password?</a>
          : <button className="btn flat" onClick={() => setRecover(true)} data-testid="forgot">Forgot password?</button>}
      </div>
      {signupEnabled
        ? <div className="switch">Don&apos;t have an account? <a className="btn flat" href="#/signup" data-testid="to-signup">Create account</a></div>
        : <>
            <div className="switch" data-testid="signup-closed-note">Accounts are created by invitation. Ask your administrator.</div>
            <div className="switch">New to Model Bench? <a className="btn flat" href={TRIAL_URL} data-testid="request-trial">Request a trial</a></div>
          </>}
      {recover && <RecoverPasswordDialog onClose={() => setRecover(false)} onReset={(u) => { setUsername(u); setLoginPrefill(""); }} />}
    </AuthCard>
  );
}
